
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserCustomerAudit from 'Models/Security/Acl/UserCustomerAudit';
import UserMonitoredEntityAudit from 'Models/Security/Acl/UserMonitoredEntityAudit';
import VisitorsMonitoredEntityAudit from 'Models/Security/Acl/VisitorsMonitoredEntityAudit';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import { customerAuditCategoryOptions } from 'Models/Enums/MappedEnumOptions';

export interface ICustomerAuditEntityAttributes extends IModelAttributes {
	description: string;
	category: Enums.customerAuditCategory;

	customerId: string;
	customer: Models.CustomerEntity | Models.ICustomerEntityAttributes;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;

}

@entity('CustomerAuditEntity', 'Customer Audit')
export default class CustomerAuditEntity extends Model
	implements ICustomerAuditEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserCustomerAudit(),
		new UserMonitoredEntityAudit(),
		new VisitorsMonitoredEntityAudit(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	@Validators.Required()
	@observable
	@attribute<CustomerAuditEntity, string>()
	@CRUD({
		name: 'Description',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public description: string;
	

	
	@Validators.Required()
	@observable
	@attribute<CustomerAuditEntity, Enums.customerAuditCategory>()
	@CRUD({
		name: 'Category',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, customerAuditCategoryOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(customerAuditCategoryOptions),
		displayFunction: (attr: Enums.customerAuditCategory) => customerAuditCategoryOptions[attr],
	})
	public category: Enums.customerAuditCategory;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Customer',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.CustomerEntity,
		
	})
	public customerId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public customer: Models.CustomerEntity;

	@observable
	@attribute()
	@CRUD({
		
		name: 'User',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.UserEntity,
		
	})
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ICustomerAuditEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ICustomerAuditEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.category !== undefined) {
				this.category = attributes.category;
			}
			if (attributes.customerId !== undefined) {
				this.customerId = attributes.customerId;
			}
			if (attributes.customer !== undefined) {
				if (attributes.customer === null) {
					this.customer = attributes.customer;
				} else if (attributes.customer instanceof Models.CustomerEntity) {
					this.customer = attributes.customer;
					this.customerId = attributes.customer.id;
				} else {
					this.customer = new Models.CustomerEntity(attributes.customer);
					this.customerId = this.customer.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		customer {
			${Models.CustomerEntity.getAttributes().join('\n')}
		}
		user {
			${Models.UserEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(CustomerAuditEntity.prototype, 'created');
CRUD(modifiedAttr)(CustomerAuditEntity.prototype, 'modified');
